import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { DocAutoToc } from "@/components/portal/DocToc";
import { PortalDocHtml } from "@/components/portal/PortalDocHtml";
import { GlofoxNav } from "@/components/portal/GlofoxNav";
import { GlofoxSidebar } from "@/components/portal/GlofoxSidebar";
import {
  GLOFOX_PORTAL_DOC_BY_ROUTE,
  type GlofoxPortalDocEntry,
} from "@/content/glofox/portal-docs/manifest";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  DOC_SECTION_SEARCH_PARAM,
  getDocSectionIdFromSearch,
  isDocSectionHashHref,
  resetDocScroll,
  scrollToDocAnchor,
} from "@/lib/portal/doc-anchor";
import { slugifyHeading } from "@/lib/portal/doc-slug";
import { getRouterBasename, rewriteSpaLinksToHashInHtml, rewriteStaticPathsInHtml } from "@/lib/portal/base-path";

function toAppPath(href: string): { pathname: string; search: string } | null {
  const hashMatch = href.match(/#(\/[^?#]*)(\?[^#]*)?/);
  if (hashMatch) {
    return {
      pathname: hashMatch[1] || "/",
      search: hashMatch[2] || "",
    };
  }

  try {
    const url = new URL(href, "https://portal.local");
    const basename = getRouterBasename();
    let pathname = url.pathname;
    if (basename && pathname.startsWith(basename)) {
      pathname = pathname.slice(basename.length) || "/";
    }
    if (!pathname.startsWith("/glofox")) {
      return null;
    }
    return { pathname, search: url.search };
  } catch {
    return null;
  }
}

interface GlofoxPortalDocPageProps {
  doc: GlofoxPortalDocEntry;
}

export default function GlofoxPortalDocPage({ doc }: GlofoxPortalDocPageProps) {
  const layoutRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sectionId = getDocSectionIdFromSearch(searchParams.toString());
  const titleId = slugifyHeading(doc.title) || "top";
  usePageTitle(doc.pageTitle);

  const sanitizedHtml = useMemo(
    () => rewriteSpaLinksToHashInHtml(rewriteStaticPathsInHtml(doc.html)),
    [doc.html],
  );

  useEffect(() => {
    const container = contentRef.current;
    if (!container) {
      return;
    }

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest("a");
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto:")) {
        return;
      }
      if (isDocSectionHashHref(href)) {
        event.preventDefault();
        const id = href.slice(1);
        navigate(
          { pathname: location.pathname, search: `?${DOC_SECTION_SEARCH_PARAM}=${encodeURIComponent(id)}` },
          { replace: true },
        );
        scrollToDocAnchor(id);
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const route = toAppPath(href);
      if (!route || !GLOFOX_PORTAL_DOC_BY_ROUTE[route.pathname]) {
        return;
      }
      event.preventDefault();
      navigate({ pathname: route.pathname, search: route.search });
    };

    container.addEventListener("click", onClick);
    return () => container.removeEventListener("click", onClick);
  }, [location.pathname, navigate, sanitizedHtml]);

  useEffect(() => {
    if (!sectionId) {
      resetDocScroll(contentRef.current);
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }
      if (scrollToDocAnchor(sectionId, "auto")) {
        return;
      }
      attempts += 1;
      if (attempts < 12) {
        requestAnimationFrame(tryScroll);
      } else {
        resetDocScroll(contentRef.current);
        navigate({ pathname: location.pathname, search: "" }, { replace: true });
      }
    };

    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
    };
  }, [doc.id, sectionId, location.pathname, navigate]);

  useEffect(() => {
    if (!location.hash || location.hash.length < 2) {
      return;
    }

    const id = location.hash.slice(1);
    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }
      if (scrollToDocAnchor(id, "auto")) {
        return;
      }
      attempts += 1;
      if (attempts < 8) {
        requestAnimationFrame(tryScroll);
      }
    };

    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
    };
  }, [doc.id, location.hash]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container || !container.querySelector(".mermaid")) {
      return;
    }

    let cancelled = false;

    void import("mermaid").then(({ default: mermaid }) => {
      if (cancelled) {
        return;
      }
      mermaid.initialize({ startOnLoad: false, theme: "neutral" });
      void mermaid.run({ nodes: container.querySelectorAll(".mermaid") });
    });

    return () => {
      cancelled = true;
    };
  }, [sanitizedHtml]);

  return (
    <>
      <GlofoxNav />
      <div className="portal-shell">
        <GlofoxSidebar />
        <main id="main-content" className="main-col">
          <div className="doc-layout" ref={layoutRef}>
            <div className="doc-lead">
              <div className="breadcrumb">
                <Link to="/" data-testid="gf-breadcrumb-home">
                  Home
                </Link>
                <span className="sep">/</span>
                {doc.route === "/glofox" ? (
                  <span data-testid="gf-breadcrumb-current">{doc.breadcrumb}</span>
                ) : (
                  <>
                    <Link to="/glofox" data-testid="gf-breadcrumb-glofox">
                      Glofox
                    </Link>
                    <span className="sep">/</span>
                    <span data-testid="gf-breadcrumb-current">{doc.breadcrumb}</span>
                  </>
                )}
              </div>

              <h1 id={titleId} data-testid={doc.testId}>
                {doc.title}
              </h1>
            </div>

            <DocAutoToc
              hidden={doc.hideToc}
              minItems={doc.minTocItems ?? 2}
              rootRef={layoutRef}
              contentKey={doc.id}
            />

            <div className="content doc-content">
              <PortalDocHtml
                html={sanitizedHtml}
                className="doc-markdown-body"
                ref={contentRef}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export function GlofoxPortalDocRoute({ route }: { route: string }) {
  const doc = GLOFOX_PORTAL_DOC_BY_ROUTE[route];
  if (!doc) {
    throw new Error(`Unknown Glofox portal doc route: ${route}`);
  }
  return <GlofoxPortalDocPage doc={doc} />;
}
